import { createFileRoute, Link } from "@tanstack/react-router";
import { getCatalogRepository } from "@/lib/catalog/repository";
import type { Collection } from "@/lib/catalog/products";
import { SafeImage } from "@/components/common/SafeImage";
import { fallbackCollectionCover } from "@/lib/catalog/fallback-images";
import { canonicalUrl } from "@/lib/seo/site";

export const Route = createFileRoute("/collections/")({
  loader: async () => {
    const repo = getCatalogRepository();
    const collections = await repo.getCollections();
    const counts = await Promise.all(
      collections.map(async (c: Collection) => [c.id as string, (await repo.getProductsByCollection(c.id)).length] as const),
    );
    return { collections, counts: Object.fromEntries(counts) as Record<string, number> };
  },
  head: () => ({
    meta: [
      { title: "Collections — The CB16 Maison Universes" },
      {
        name: "description",
        content:
          "Explore the CB16 collections — Signatures, Absolus, Voyages, Nocturnes, and Editions Limitées. Each a distinct olfactive universe from our atelier in Mumbai, India.",
      },
      { property: "og:title", content: "Collections — CB16 Maison" },
      {
        property: "og:description",
        content: "Five collections, five olfactive universes from the CB16 atelier.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonicalUrl("/collections") },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: canonicalUrl("/collections") }],
  }),
  component: CollectionsPage,
});

function CollectionsPage() {
  const { collections, counts } = Route.useLoaderData();
  return (
    <div className="bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <section className="border-b border-black/10 bg-[#faf7f2]">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
          <span className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">
            Explore
          </span>
          <h1
            className="mt-4 text-4xl font-normal leading-tight text-black sm:text-5xl lg:text-6xl"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Collections
          </h1>
          <div className="mt-6 h-px w-10 bg-[#C6A15B]" />
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-black/60 sm:text-base">
            Olfactive universes composed at the CB16 atelier — enter one to see every product in
            that family, across every category.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:gap-12">
          {collections.map((c: Collection) => {
            const count = counts[c.id] ?? 0;
            return (
              <Link
                key={c.id}
                to="/collections/$slug"
                params={{ slug: c.id }}
                className="group relative block overflow-hidden bg-[#faf7f2]"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <SafeImage
                    src={c.cover}
                    fallback={fallbackCollectionCover(c.id)}
                    alt={`${c.name} collection`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-8 text-white">
                  <span className="text-[11px] uppercase tracking-[0.28em] text-[#E8C687]">
                    {count} {count === 1 ? "product" : "products"}
                  </span>
                  <h2
                    className="text-3xl font-normal sm:text-4xl"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {c.name}
                  </h2>
                  <p className="max-w-md text-sm text-white/80">{c.description}</p>
                  <span className="mt-2 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-white/90 transition-colors group-hover:text-[#E8C687]">
                    Explore
                    <span className="h-px w-8 bg-[#C6A15B] transition-all group-hover:w-14" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
